import { Banner, Button } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { type ThemeColor, useAppName } from '@vrplatform/theme';
import {
  ActionIcon,
  Box,
  Center,
  Divider,
  Group,
  Text,
  Tooltip,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { Notification } from '@mantine/core';
import { useSessionStorage } from '@mantine/hooks';
import { type ComponentProps, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useConnectionDetailDrawer } from '../hooks/useConnectionDetailDrawer';
import { type IssueRow, useTeamIssues } from '../hooks';

type Props = {
  type: 'card' | 'affix';
};

// Bank feed problems show only on the bank connection badge and its popover.
type BlockingIssueRow = Omit<IssueRow, 'type'> & {
  type: Exclude<IssueRow['type'], 'bank-feed-error'>;
};

export const PmErrorIssueNotification = ({ type }: Props) => {
  const list = useTeamIssues().list.filter(
    (issue): issue is BlockingIssueRow => issue.type !== 'bank-feed-error'
  );
  const location = useLocation();
  const goto = useNavigate();
  const [{ id: teamId, status }] = useTeam();
  const [hideAffixBySession, setHideAffixBySession] = useSessionStorage({
    key: `${teamId}-pmun`, // pmun = pm urgent notification
    defaultValue: false,
  });

  const color: ThemeColor =
    list.length === 1 && list[0].type === 'connection-version-outdated'
      ? 'yellow'
      : 'red';

  if (type === 'card') {
    return (
      <Banner
        mounted={!!list.length}
        type={color === 'red' ? 'error' : 'warning'}
      >
        {list.length === 1 ? (
          <SingleIssueContent {...list[0]} />
        ) : (
          <MultiIssueContent />
        )}
      </Banner>
    );
  }

  const hideAffix =
    status === 'inactive' ||
    hideAffixBySession ||
    !list.length ||
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/connections');
  location.pathname === '/';

  if (hideAffix) return null;

  return (
    <Notification
      radius={'xl'}
      icon={
        <Icon
          icon="AlertCircleIcon"
          size={18}
          color={(theme) => theme.colors[color][8]}
        />
      }
      withCloseButton={false}
      styles={(theme) => ({
        root: {
          position: 'absolute',
          transition: 'left 0.3s ease-out',
          top: theme.spacing.sm,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: theme.colors[color][0],
          border: `1px solid ${theme.colors[color][3]}`,
          zIndex: 10000,
          paddingRight: 0,
        },
        icon: {
          backgroundColor: 'transparent',
          marginRight: theme.spacing.xs,
        },
        description: {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: theme.spacing.xs,
        },
      })}
    >
      {list.length === 1 ? (
        <NotificationContent {...list[0]} />
      ) : (
        <Group wrap="nowrap">
          <Text component="span" fw={500}>
            We've found multiple issues that are blocking your data processing.
          </Text>
          <Box
            component="button"
            onClick={() => goto('/connections')}
            size="xs"
            variant="light"
            sx={(theme) => ({
              color: `${theme.colors[color][9]}!important`,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: rem(5),
              padding: 0,
              flexWrap: 'nowrap',
              ':active': {
                transform: 'translateY(1px)',
              },
            })}
            color={color}
          >
            <Text component="span" fw={500}>
              View issues
            </Text>
            <Icon
              icon="ArrowRightIcon"
              size={12}
              color={(theme) => theme.colors[color][9]}
              strokeWidth={2.5}
            />
          </Box>
        </Group>
      )}
      <Divider
        orientation="vertical"
        ml={rem(5)}
        sx={(theme) => ({
          borderColor: theme.colors[color][3],
        })}
      />
      <SnoozeUrgentIssueAffixButton
        setHideAffix={() => setHideAffixBySession(true)}
        color={color}
      />
    </Notification>
  );
};

const SnoozeUrgentIssueAffixButton = ({
  setHideAffix,
  color,
}: {
  setHideAffix: () => void;
  color: ThemeColor;
}) => {
  return (
    <Tooltip label="Snooze" withinPortal withArrow zIndex={10001}>
      <ActionIcon onClick={setHideAffix} variant="transparent">
        <Icon
          icon="ClockZzzIcon"
          size={16}
          color={(theme) => theme.colors[color][9]}
        />
      </ActionIcon>
    </Tooltip>
  );
};

function useIssueContent({ message, type, itemName, id }: BlockingIssueRow) {
  const goto = useNavigate();
  const { open: openConnectionDetailDrawer } = useConnectionDetailDrawer();

  return useMemo(() => {
    const options: Record<
      BlockingIssueRow['type'],
      ComponentProps<typeof Content>
    > = {
      'connection-error': {
        title: `We encountered issues when syncing with ${itemName}`,
        message,
        buttonLabel: 'Reconnect',
        onClick: () => {
          goto(`/connections`);
          openConnectionDetailDrawer(id, 'replace');
        },
      },
      'connection-version-outdated': {
        title: `Outdated connection for ${itemName}`,
        message,
        buttonLabel: 'Reconnect',
        color: 'yellow',
        onClick: () => {
          goto(`/connections`);
          openConnectionDetailDrawer(id, 'replace');
        },
      },
    };

    return options[type];
  }, [message, type, itemName, id, goto, openConnectionDetailDrawer]);
}

const NotificationContent = (issue: BlockingIssueRow) => {
  const content = useIssueContent(issue);

  const color: ThemeColor =
    issue.type === 'connection-version-outdated' ? 'yellow' : 'red';

  return (
    <Group wrap="nowrap">
      <Text size="sm" fw={500}>
        {content.title}
      </Text>

      <Box
        component="button"
        onClick={content.onClick}
        size="xs"
        variant="light"
        sx={(theme) => ({
          color: `${theme.colors[color][9]}!important`,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: rem(5),
          padding: 0,
          flexWrap: 'nowrap',
          ':active': {
            transform: 'translateY(1px)',
          },
        })}
        color={color}
      >
        <Text component="span" fw={500}>
          {content.buttonLabel}
        </Text>

        <Icon
          icon="ArrowRightIcon"
          size={12}
          color={(theme) => theme.colors[color][9]}
          strokeWidth={2.5}
        />
      </Box>
    </Group>
  );
};

const MultiIssueContent = () => {
  const { appName } = useAppName();

  const goto = useNavigate();

  const onClick = () => {
    goto('/connections');
  };

  return (
    <Content
      title="Blocking issues"
      message={`We've found multiple issues that are blocking your data processing. Please review and fix them to continue using ${appName}.`}
      buttonLabel="View issues"
      onClick={onClick}
    />
  );
};

const SingleIssueContent = (issue: BlockingIssueRow) => {
  const content = useIssueContent(issue);

  return <Content {...content} />;
};

const Content = ({
  buttonLabel,
  message,
  onClick,
  title,
  color = 'red',
}: {
  title: string;
  message: string;
  buttonLabel: string;
  onClick: () => void;
  color?: ThemeColor;
}) => {
  const { colors } = useMantineTheme();
  const c = colors[color][7];

  return (
    <>
      <Center>
        <Icon icon="AlertTriangleIcon" size={32} color={c} />
      </Center>
      <Box sx={{ flex: 1 }}>
        <Text size="lg" fw={500} c={c}>
          {title}
        </Text>
        <Text size="sm" c="neutral">
          {message}
        </Text>
      </Box>
      <Button onClick={onClick}>{buttonLabel}</Button>
    </>
  );
};
